import type { TableView, TableViewPrefs, ViewColumn, ViewContent, ViewSort } from './types';
import { DEFAULT_PAGE_SIZE } from './types';
import { normalizeColumnWidth } from './columnOps';

/**
 * Rules for the tab strip: which tabs show and in what order, what a user may do to each, and when
 * a tab counts as modified. Pure, so they're tested without React or storage.
 */

export const newViewId = (): string => `view_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

/**
 * Only the owner saves, renames, shares or deletes a view; colleagues use, tweak, copy or hide it.
 * The backend's `editable` decides where there is one (it also covers runAs and deactivated owners).
 */
export const isOwnView = (view: TableView, userId: string): boolean => view.editable ?? view.ownerId === userId;

/**
 * The tabs a user sees, in their order. Views missing from the stored order (new ones, or shared with
 * them since) go to the end in creation order, so a tab never silently goes missing; hidden ones and
 * stale ids drop out.
 */
export const orderedViews = (views: TableView[], prefs: TableViewPrefs): TableView[] => {
  const byId = new Map(views.map(view => [view.id, view]));
  const hidden = new Set(prefs.hidden);
  const listed = prefs.order.filter(id => byId.has(id));
  const listedSet = new Set(listed);
  const unlisted = views
    .filter(view => !listedSet.has(view.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map(view => view.id);
  return [...listed, ...unlisted].filter(id => !hidden.has(id)).map(id => byId.get(id));
};

/** The colleagues' tabs this user hid, in their stored order; ids of views that are gone drop out. */
export const hiddenViews = (views: TableView[], prefs: TableViewPrefs): TableView[] => {
  const byId = new Map(views.map(view => [view.id, view]));
  return prefs.hidden.filter(id => byId.has(id)).map(id => byId.get(id));
};

/**
 * Shows a hidden tab again and opens it. It returns to the place it had in the order (hiding kept it
 * there); one that never had a place goes to the end, as a newly shared tab does.
 */
export const unhideView = (prefs: TableViewPrefs, viewId: string): TableViewPrefs => ({
  ...prefs,
  hidden: prefs.hidden.filter(id => id !== viewId),
  activeViewId: viewId,
});

/** The tab to show: the remembered one while it's still visible, else the first. */
export const resolveActiveViewId = (visible: TableView[], prefs: TableViewPrefs): string | undefined =>
  visible.some(view => view.id === prefs.activeViewId) ? prefs.activeViewId : visible[0]?.id;

/** Moves a tab one step left (-1) or right (+1) in the user's order; the ends don't wrap. */
export const moveView = (visible: TableView[], prefs: TableViewPrefs, viewId: string, direction: -1 | 1): TableViewPrefs => {
  const ids = visible.map(view => view.id);
  const from = ids.indexOf(viewId);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= ids.length) return prefs;
  [ids[from], ids[to]] = [ids[to], ids[from]];
  // Hidden views keep their place in the stored order, so unhiding one puts it back where it was.
  const hiddenKept = prefs.order.filter(id => prefs.hidden.includes(id));
  return { ...prefs, order: [...ids, ...hiddenKept.filter(id => !ids.includes(id))] };
};

export const canMoveView = (visible: TableView[], viewId: string, direction: -1 | 1): boolean => {
  const index = visible.findIndex(view => view.id === viewId);
  return index !== -1 && index + direction >= 0 && index + direction < visible.length;
};

/** The last tab can't go: a table without a tab has nothing to show its filters, sort or columns. */
export const canRemoveView = (visible: TableView[]): boolean => visible.length > 1;

/** What removing does: the owner deletes the view, a colleague hides the shared one for themselves. */
export const removeActionFor = (view: TableView, userId: string): 'delete' | 'hide' => (isOwnView(view, userId) ? 'delete' : 'hide');

/** A name the strip can show: trimmed, never empty, and short enough not to push the other tabs away. */
export const MAX_VIEW_NAME_LENGTH = 60;
export const normalizeViewName = (name: string): string | undefined => {
  const trimmed = (name ?? '').trim().replace(/\s+/g, ' ');
  return trimmed.length > 0 ? trimmed.slice(0, MAX_VIEW_NAME_LENGTH) : undefined;
};

export const createView = (args: { tableKey: string; name: string; ownerId: string; content: ViewContent; now: string; id?: string }): TableView => ({
  id: args.id ?? newViewId(),
  tableKey: args.tableKey,
  name: normalizeViewName(args.name) ?? args.name,
  ownerId: args.ownerId,
  visibility: 'PRIVATE',
  ...cloneContent(args.content),
  createdAt: args.now,
  updatedAt: args.now,
  version: 1,
});

/** A copy always belongs to whoever made it and starts private — copying a shared tab is how a colleague makes it their own. */
export const duplicateView = (source: TableView, args: { name: string; ownerId: string; now: string; id?: string }): TableView =>
  createView({ tableKey: source.tableKey, name: args.name, ownerId: args.ownerId, content: source, now: args.now, id: args.id });

/** The view with new content, still at the version last read — the store sets the next one. */
export const saveViewContent = (view: TableView, content: ViewContent, now: string): TableView => ({
  ...view,
  ...cloneContent(content),
  updatedAt: now,
});

export const cloneContent = (content: ViewContent): ViewContent => ({
  filter: { ...(content.filter ?? {}) },
  sort: { ...content.sort },
  columns: content.columns.map(column => ({
    id: column.id,
    members: [...column.members],
    ...(column.title ? { title: column.title } : {}),
    ...(column.width ? { width: column.width } : {}),
  })),
  ...(content.favouriteFilters !== undefined ? { favouriteFilters: [...content.favouriteFilters] } : {}),
  ...(content.pageSize !== undefined ? { pageSize: content.pageSize } : {}),
  ...(content.pagingMode !== undefined ? { pagingMode: content.pagingMode } : {}),
});

/** Same filter meaning: cleared keys (null, '', []) count as absent, and key order doesn't matter. */
const meaningfulFilter = (filter: Record<string, unknown> = {}): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(filter)
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0))
      .sort(([a], [b]) => a.localeCompare(b)),
  );

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

const sameSort = (a: ViewSort, b: ViewSort): boolean => a?.sortField === b?.sortField && a?.sortDirection === b?.sortDirection;

/** Column ids are bookkeeping: the same stacks with the same headings and widths, in the same order, are the same layout. */
const sameColumns = (a: ViewColumn[], b: ViewColumn[]): boolean =>
  sameJson(
    a.map(column => [column.members, column.title ?? null, column.width ?? null]),
    b.map(column => [column.members, column.title ?? null, column.width ?? null]),
  );

const sameFavourites = (a?: string[], b?: string[]): boolean => (a === undefined || b === undefined ? a === b : sameJson([...a].sort(), [...b].sort()));

/** Which parts of a working copy differ from the saved tab — the tab shows as modified when any does. */
export const diffContent = (saved: ViewContent, working: ViewContent) => ({
  filter: !sameJson(meaningfulFilter(saved.filter), meaningfulFilter(working.filter)),
  sort: !sameSort(saved.sort, working.sort),
  columns: !sameColumns(saved.columns, working.columns),
  favouriteFilters: !sameFavourites(saved.favouriteFilters, working.favouriteFilters),
  // A tab saved before it had a page size shows the default, so the default is no change.
  pageSize: (saved.pageSize ?? DEFAULT_PAGE_SIZE) !== (working.pageSize ?? DEFAULT_PAGE_SIZE),
  pagingMode: (saved.pagingMode ?? 'pages') !== (working.pagingMode ?? 'pages'),
});

export const isModified = (saved: ViewContent, working: ViewContent): boolean => Object.values(diffContent(saved, working)).some(Boolean);

/**
 * "Uložit filtr" in the default-filters sidebar: the working copy's filter, sort and hearted filters,
 * on top of the tab's saved columns — an unsaved column edit stays an unsaved edit.
 */
export const withFiltersFrom = (saved: ViewContent, working: ViewContent): ViewContent => ({
  ...cloneContent(saved),
  filter: { ...(working.filter ?? {}) },
  sort: { ...working.sort },
  ...(working.favouriteFilters !== undefined ? { favouriteFilters: [...working.favouriteFilters] } : { favouriteFilters: undefined }),
});

/** Pages or scroll on a colleague's tab, as this user sees it: their own choice, else the owner's. */
export const viewerPagingMode = (view: TableView, prefs: TableViewPrefs): 'pages' | 'scroll' => prefs.pagingModes?.[view.id] ?? view.pagingMode ?? 'pages';

/** The user's choice of pages or scroll for a colleague's tab; choices for tabs gone from the list are dropped. */
export const withPagingOverride = (prefs: TableViewPrefs, viewId: string, mode: 'pages' | 'scroll', viewIds: string[]): TableViewPrefs => {
  const known = new Set(viewIds);
  const kept = Object.entries(prefs.pagingModes ?? {}).filter(([id]) => known.has(id) && id !== viewId);
  return { ...prefs, pagingModes: Object.fromEntries([...kept, [viewId, mode]]) };
};

/** A colleague's tab's columns with the widths this user dragged them to over the owner's. */
export const withViewerWidths = (columns: ViewColumn[], prefs: TableViewPrefs, viewId: string): ViewColumn[] => {
  const widths = prefs.columnWidths?.[viewId];
  if (!widths) return columns;
  return columns.map(column => (widths[column.id] ? { ...column, width: widths[column.id] } : column));
};

/**
 * The user's width for a column of a colleague's tab (`undefined`: back to the owner's). Widths for
 * tabs gone from the list are dropped.
 */
export const withWidthOverride = (prefs: TableViewPrefs, viewId: string, columnId: string, width: number | undefined, viewIds: string[]): TableViewPrefs => {
  const known = new Set(viewIds);
  const views = Object.fromEntries(Object.entries(prefs.columnWidths ?? {}).filter(([id]) => known.has(id)));
  const { [columnId]: _previous, ...others } = views[viewId] ?? {};
  const clean = normalizeColumnWidth(width);
  const forView = clean ? { ...others, [columnId]: clean } : others;
  const { [viewId]: _old, ...rest } = views;
  return { ...prefs, columnWidths: Object.keys(forView).length > 0 ? { ...rest, [viewId]: forView } : rest };
};
